import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Home as HomeIcon, Briefcase, User, Phone, MessageCircle, Sparkles, Navigation, ChevronLeft, CheckCircle2, Clock, Star, Truck, MapPin, TrendingUp, ArrowRight, Signal, Wifi, Battery, Power, LogOut, Settings, CreditCard, Shield } from 'lucide-react';
import { AskCustomerAiSlider } from '../components/AskCustomerAiSlider';
type DriverStatus = 'incoming' | 'accepted' | 'in_progress' | 'at_pickup' | 'picked' | 'at_dropoff' | 'completed';
type DriverJob = {
  id: string;
  service: string;
  customerName: string;
  customerPhone: string;
  pickup: string;
  dropoff: string;
  scheduled: string;
  price: number;
  status: DriverStatus;
};
const LIFECYCLE: Array<{
  id: DriverStatus;
  label: string;
}> = [{
  id: 'accepted',
  label: 'Accepted'
}, {
  id: 'in_progress',
  label: 'In progress'
}, {
  id: 'at_pickup',
  label: 'At pickup'
}, {
  id: 'picked',
  label: 'Picked'
}, {
  id: 'at_dropoff',
  label: 'At dropoff'
}, {
  id: 'completed',
  label: 'Completed'
}];
function getNextStatus(s: DriverStatus): DriverStatus | null {
  const order: DriverStatus[] = ['incoming', 'accepted', 'in_progress', 'at_pickup', 'picked', 'at_dropoff', 'completed'];
  const idx = order.indexOf(s);
  if (idx === -1 || idx >= order.length - 1) return null;
  return order[idx + 1];
}
function getPrimaryCTA(s: DriverStatus): string {
  switch (s) {
    case 'incoming':
      return 'Accept job';
    case 'accepted':
      return 'Start trip';
    case 'in_progress':
      return "I'm at pickup";
    case 'at_pickup':
      return 'Picked up';
    case 'picked':
      return "I'm at dropoff";
    case 'at_dropoff':
      return 'Mark completed';
    default:
      return 'Done';
  }
}
function getStatusPill(s: DriverStatus): {
  label: string;
  className: string;
} {
  switch (s) {
    case 'incoming':
      return {
        label: 'New offer',
        className: 'bg-amber-500/10 text-amber-300 ring-1 ring-amber-400/30'
      };
    case 'accepted':
      return {
        label: 'Accepted',
        className: 'bg-blue-500/10 text-blue-300 ring-1 ring-blue-400/30'
      };
    case 'completed':
      return {
        label: 'Completed',
        className: 'bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-400/30'
      };
    default:
      return {
        label: 'In progress',
        className: 'bg-brand-500/15 text-brand-300 ring-1 ring-brand-400/30'
      };
  }
}
const INITIAL_JOBS: DriverJob[] = [{
  id: 'JOB-2041',
  service: 'Apartment Move',
  customerName: 'Layla Hassan',
  customerPhone: '+971 50 234 7812',
  pickup: 'Downtown Dubai · Burj Views Tower 2',
  dropoff: 'Jumeirah Lakes Towers · Cluster G',
  scheduled: 'Today, 11:30 AM',
  price: 540,
  status: 'incoming'
}, {
  id: 'JOB-2038',
  service: 'Furniture Delivery',
  customerName: 'Omar Saleh',
  customerPhone: '+971 56 902 1144',
  pickup: 'IKEA Festival City',
  dropoff: 'Al Barsha South 3, Villa 22',
  scheduled: 'Today, 1:45 PM',
  price: 220,
  status: 'accepted'
}, {
  id: 'JOB-2033',
  service: 'Vehicle Recovery',
  customerName: 'Noor Al Awadi',
  customerPhone: '+971 55 778 4321',
  pickup: 'Sheikh Zayed Rd, Exit 41',
  dropoff: 'AAA Auto Service · Al Quoz 3',
  scheduled: 'Today, 9:10 AM',
  price: 310,
  status: 'in_progress'
}, {
  id: 'JOB-2028',
  service: 'Office Move',
  customerName: 'Faisal Mansour',
  customerPhone: '+971 52 901 0044',
  pickup: 'Business Bay · Bay Square 9',
  dropoff: 'DIFC Gate Village 4',
  scheduled: 'Yesterday',
  price: 880,
  status: 'completed'
}];
export function DriverApp() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'home' | 'jobs' | 'profile'>('home');
  const [online, setOnline] = useState(true);
  const [jobs, setJobs] = useState<DriverJob[]>(INITIAL_JOBS);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [aiOpenFor, setAiOpenFor] = useState<string | null>(null);
  const activeJob = useMemo(() => jobs.find(j => ['accepted', 'in_progress', 'at_pickup', 'picked', 'at_dropoff'].includes(j.status)), [jobs]);
  const incoming = jobs.filter(j => j.status === 'incoming');
  const queue = jobs.filter(j => ['accepted', 'in_progress', 'at_pickup', 'picked', 'at_dropoff'].includes(j.status));
  const completed = jobs.filter(j => j.status === 'completed');
  const openJob = jobs.find(j => j.id === openJobId) || null;
  const aiJob = jobs.find(j => j.id === aiOpenFor) || null;
  const advance = (id: string) => {
    setJobs(prev => prev.map(j => {
      if (j.id !== id) return j;
      const next = getNextStatus(j.status);
      if (!next) return j;
      return {
        ...j,
        status: next
      };
    }));
  };
  const declineJob = (id: string) => {
    setJobs(prev => prev.filter(j => j.id !== id));
  };
  return <div className="min-h-screen bg-gradient-to-b from-gray-950 to-gray-900 flex flex-col items-center justify-center py-8 px-4 relative" data-id="element-1107">
      <button onClick={() => navigate('/')} className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 ring-1 ring-white/10 text-white text-xs font-bold transition-colors backdrop-blur-sm" data-id="element-1108">
        <ChevronLeft className="w-3.5 h-3.5" data-id="element-1109" />
        All modules
      </button>
      <PhoneFrame data-id="element-1110">
        <div className="flex-1 overflow-y-auto scrollbar-hide bg-gradient-to-b from-gray-950 via-gray-900 to-black text-white" data-id="element-1111">
          <AnimatePresence mode="wait" data-id="element-1112">
            <motion.div key={tab} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.18
          }} data-id="element-1113">
              {tab === 'home' && <HomeTab online={online} setOnline={setOnline} activeJob={activeJob || null} queueCount={queue.length} completedCount={completed.length} onOpenJob={setOpenJobId} data-id="element-1114" />}
              {tab === 'jobs' && <JobsTab incoming={incoming} queue={queue} completed={completed} onAccept={advance} onDecline={declineJob} onOpenJob={setOpenJobId} data-id="element-1115" />}
              {tab === 'profile' && <ProfileTab completedCount={completed.length} onSignOut={() => navigate('/')} data-id="element-1116" />}
            </motion.div>
          </AnimatePresence>
        </div>
        <BottomNav tab={tab} onChange={setTab} data-id="element-1117" />
      </PhoneFrame>

      {/* Job detail overlay */}
      <AnimatePresence data-id="element-1118">
        {openJob && <JobDetailOverlay job={openJob} onClose={() => setOpenJobId(null)} onAdvance={() => advance(openJob.id)} onAskAi={() => setAiOpenFor(openJob.id)} data-id="element-1119" />}
      </AnimatePresence>

      {/* AI slider */}
      <AskCustomerAiSlider open={!!aiJob} customerName={aiJob?.customerName || 'Customer'} contextLabel={aiJob?.id} onClose={() => setAiOpenFor(null)} data-id="element-1120" />
    </div>;
}
function PhoneFrame({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="w-[375px] h-[812px] bg-black rounded-[3rem] shadow-2xl overflow-hidden border-[10px] border-gray-800 relative flex flex-col" data-id="element-1121">
      {/* Status bar */}
      <div className="h-11 w-full flex justify-between items-center px-7 pt-2 z-20 text-white text-[13px] font-semibold shrink-0" data-id="element-1122">
        <span data-id="element-1123">9:41</span>
        <div className="w-32 h-7 bg-black rounded-full absolute left-1/2 -translate-x-1/2 top-2" data-id="element-1124"></div>
        <div className="flex items-center gap-1.5" data-id="element-1125">
          <Signal className="w-3.5 h-3.5" data-id="element-1126" />
          <Wifi className="w-3.5 h-3.5" data-id="element-1127" />
          <Battery className="w-4 h-4" data-id="element-1128" />
        </div>
      </div>
      {children}
    </div>;
}
function BottomNav({
  tab,
  onChange
}: {
  tab: 'home' | 'jobs' | 'profile';
  onChange: (t: 'home' | 'jobs' | 'profile') => void;
}) {
  const items: Array<{
    id: 'home' | 'jobs' | 'profile';
    label: string;
    icon: typeof HomeIcon;
  }> = [{
    id: 'home',
    label: 'Home',
    icon: HomeIcon
  }, {
    id: 'jobs',
    label: 'Jobs',
    icon: Briefcase
  }, {
    id: 'profile',
    label: 'Profile',
    icon: User
  }];
  return <div className="shrink-0 bg-black/80 backdrop-blur-xl border-t border-white/5 px-6 pt-2 pb-6 flex justify-around" data-id="element-1129">
      {items.map(it => {
      const Icon = it.icon;
      const active = tab === it.id;
      return <button key={it.id} onClick={() => onChange(it.id)} className="flex flex-col items-center gap-1 transition-colors" data-id="element-1130">
            <Icon className={`w-5 h-5 ${active ? 'text-brand-400' : 'text-gray-500'}`} data-id="element-1131" />
            <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? 'text-brand-400' : 'text-gray-500'}`} data-id="element-1132">
              {it.label}
            </span>
          </button>;
    })}
    </div>;
}
function HomeTab({
  online,
  setOnline,
  activeJob,
  queueCount,
  completedCount,
  onOpenJob
}: {
  online: boolean;
  setOnline: (v: boolean) => void;
  activeJob: DriverJob | null;
  queueCount: number;
  completedCount: number;
  onOpenJob: (id: string) => void;
}) {
  return <div className="p-6 pb-8 space-y-6" data-id="element-1133">
      {/* Online toggle + greeting */}
      <div data-id="element-1134">
        <button onClick={() => setOnline(!online)} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${online ? 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30' : 'bg-gray-800 text-gray-400 ring-1 ring-gray-700'}`} data-id="element-1135">
          <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} data-id="element-1136" />
          {online ? 'Online · accepting jobs' : 'Offline'}
          <Power className="w-3 h-3" data-id="element-1137" />
        </button>
        <h1 className="text-2xl font-display font-bold mt-3 tracking-tight" data-id="element-1138">
          Good morning,
          <br data-id="element-1139" />
          <span className="text-brand-400" data-id="element-1140">Ahmed</span>
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3" data-id="element-1141">
        <StatCard label="Today's earnings" value="AED 470" icon={TrendingUp} accent="emerald" data-id="element-1142" />
        <StatCard label="Jobs in queue" value={String(queueCount)} icon={Briefcase} accent="brand" data-id="element-1143" />
      </div>

      {/* Active job */}
      <div data-id="element-1144">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3" data-id="element-1145">
          Active Job
        </h2>
        {activeJob ? <button onClick={() => onOpenJob(activeJob.id)} className="w-full text-left bg-gradient-to-br from-gray-800/60 to-gray-900 border border-white/5 rounded-3xl p-5 active:scale-[0.99] transition-transform" data-id="element-1146">
            <div className="flex items-center justify-between mb-3" data-id="element-1147">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${getStatusPill(activeJob.status).className}`} data-id="element-1148">
                {getStatusPill(activeJob.status).label}
              </span>
              <span className="text-[10px] text-gray-500" data-id="element-1149">{activeJob.id}</span>
            </div>
            <h3 className="font-bold text-white text-base mb-1" data-id="element-1150">
              {activeJob.service}
            </h3>
            <p className="text-xs text-gray-400 mb-3" data-id="element-1151">
              {activeJob.customerName}
            </p>
            <div className="text-xs text-gray-300 flex items-center gap-1.5 mb-1 truncate" data-id="element-1152">
              <MapPin className="w-3 h-3 text-brand-400 shrink-0" data-id="element-1153" />
              <span className="truncate" data-id="element-1154">{activeJob.pickup}</span>
            </div>
            <div className="text-xs text-gray-300 flex items-center gap-1.5 truncate" data-id="element-1155">
              <MapPin className="w-3 h-3 text-emerald-400 shrink-0" data-id="element-1156" />
              <span className="truncate" data-id="element-1157">{activeJob.dropoff}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between" data-id="element-1158">
              <span className="text-sm font-bold text-white" data-id="element-1159">
                AED {activeJob.price}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-brand-400 font-bold" data-id="element-1160">
                {getPrimaryCTA(activeJob.status)}
                <ArrowRight className="w-3.5 h-3.5" data-id="element-1161" />
              </span>
            </div>
          </button> : <div className="bg-gray-900/60 border border-white/5 rounded-3xl p-8 text-center text-gray-400 text-sm" data-id="element-1162">
            No active job — you're all caught up.
          </div>}
      </div>

      {/* Footer stat */}
      <div className="text-center text-xs text-gray-500" data-id="element-1163">
        {completedCount} jobs completed lifetime · ⭐ 4.92 rating
      </div>
    </div>;
}
function StatCard({
  label,
  value,
  icon: Icon,
  accent
}: {
  label: string;
  value: string;
  icon: typeof TrendingUp;
  accent: 'emerald' | 'brand';
}) {
  const accentMap = {
    emerald: 'text-emerald-400 bg-emerald-500/10',
    brand: 'text-brand-400 bg-brand-500/10'
  };
  return <div className="bg-gray-900/60 border border-white/5 rounded-2xl p-4" data-id="element-1164">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${accentMap[accent]}`} data-id="element-1165">
        <Icon className="w-4 h-4" data-id="element-1166" />
      </div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5" data-id="element-1167">
        {label}
      </p>
      <p className="text-lg font-display font-bold text-white" data-id="element-1168">{value}</p>
    </div>;
}
function JobsTab({
  incoming,
  queue,
  completed,
  onAccept,
  onDecline,
  onOpenJob
}: {
  incoming: DriverJob[];
  queue: DriverJob[];
  completed: DriverJob[];
  onAccept: (id: string) => void;
  onDecline: (id: string) => void;
  onOpenJob: (id: string) => void;
}) {
  return <div className="p-6 pb-8 space-y-6" data-id="element-1169">
      <h1 className="text-2xl font-display font-bold tracking-tight" data-id="element-1170">Jobs</h1>

      {/* Incoming */}
      <section data-id="element-1171">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3 flex items-center gap-2" data-id="element-1172">
          Incoming offers
          <span className="px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px]" data-id="element-1173">
            {incoming.length}
          </span>
        </h2>
        {incoming.length === 0 ? <p className="text-xs text-gray-500" data-id="element-1174">No new offers right now.</p> : <div className="space-y-3" data-id="element-1175">
            {incoming.map(j => <div key={j.id} className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-400/20 rounded-2xl p-4" data-id="element-1176">
                <JobCardBody job={j} onOpen={() => onOpenJob(j.id)} data-id="element-1177" />
                <div className="flex gap-2 mt-3" data-id="element-1178">
                  <button onClick={() => onDecline(j.id)} className="flex-1 py-2 rounded-lg bg-white/5 text-gray-300 text-xs font-bold border border-white/5" data-id="element-1179">
                    Decline
                  </button>
                  <button onClick={() => onAccept(j.id)} className="flex-1 py-2 rounded-lg bg-brand-500 hover:bg-brand-400 text-white text-xs font-bold transition-colors" data-id="element-1180">
                    Accept
                  </button>
                </div>
              </div>)}
          </div>}
      </section>

      {/* Queue */}
      <section data-id="element-1181">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3" data-id="element-1182">
          My queue
        </h2>
        {queue.length === 0 ? <p className="text-xs text-gray-500" data-id="element-1183">No active jobs in your queue.</p> : <div className="space-y-3" data-id="element-1184">
            {queue.map(j => <button key={j.id} onClick={() => onOpenJob(j.id)} className="w-full text-left bg-gray-900/60 border border-white/5 rounded-2xl p-4 active:scale-[0.99] transition-transform" data-id="element-1185">
                <JobCardBody job={j} onOpen={() => onOpenJob(j.id)} data-id="element-1186" />
              </button>)}
          </div>}
      </section>

      {/* History */}
      <section data-id="element-1187">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3" data-id="element-1188">
          Recent history
        </h2>
        {completed.length === 0 ? <p className="text-xs text-gray-500" data-id="element-1189">No completed jobs yet.</p> : <div className="space-y-2" data-id="element-1190">
            {completed.map(j => <button key={j.id} onClick={() => onOpenJob(j.id)} className="w-full text-left bg-gray-900/40 border border-white/5 rounded-xl p-3 flex items-center justify-between" data-id="element-1191">
                <div className="min-w-0" data-id="element-1192">
                  <p className="text-sm font-bold text-white truncate" data-id="element-1193">
                    {j.service}
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-1194">
                    {j.scheduled} · {j.customerName}
                  </p>
                </div>
                <div className="text-right shrink-0" data-id="element-1195">
                  <p className="text-sm font-bold text-emerald-400" data-id="element-1196">
                    AED {j.price}
                  </p>
                  <p className="text-[10px] text-gray-500" data-id="element-1197">{j.id}</p>
                </div>
              </button>)}
          </div>}
      </section>
    </div>;
}
function JobCardBody({
  job,
  onOpen
}: {
  job: DriverJob;
  onOpen: () => void;
}) {
  const pill = getStatusPill(job.status);
  return <div data-id="element-1198">
      <div className="flex items-center justify-between mb-2" data-id="element-1199">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${pill.className}`} data-id="element-1200">
          {pill.label}
        </span>
        <span className="text-[10px] text-gray-500" data-id="element-1201">{job.id}</span>
      </div>
      <h3 className="font-bold text-white text-sm mb-0.5" data-id="element-1202">{job.service}</h3>
      <p className="text-[11px] text-gray-400 mb-2" data-id="element-1203">{job.customerName}</p>
      <div className="text-xs text-gray-300 flex items-center gap-1.5 truncate mb-0.5" data-id="element-1204">
        <MapPin className="w-3 h-3 text-brand-400 shrink-0" data-id="element-1205" />
        <span className="truncate" data-id="element-1206">{job.pickup}</span>
      </div>
      <div className="text-xs text-gray-300 flex items-center gap-1.5 truncate mb-2" data-id="element-1207">
        <MapPin className="w-3 h-3 text-emerald-400 shrink-0" data-id="element-1208" />
        <span className="truncate" data-id="element-1209">{job.dropoff}</span>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-white/5" data-id="element-1210">
        <span className="text-[11px] text-gray-400 flex items-center gap-1" data-id="element-1211">
          <Clock className="w-3 h-3" data-id="element-1212" /> {job.scheduled}
        </span>
        <span className="text-sm font-bold text-white" data-id="element-1213">AED {job.price}</span>
      </div>
    </div>;
}
function ProfileTab({
  completedCount,
  onSignOut
}: {
  completedCount: number;
  onSignOut: () => void;
}) {
  return <div className="p-6 pb-8 space-y-6" data-id="element-1214">
      <h1 className="text-2xl font-display font-bold tracking-tight" data-id="element-1215">
        Profile
      </h1>

      <div className="bg-gradient-to-br from-gray-800/60 to-gray-900 border border-white/5 rounded-3xl p-5 flex items-center gap-4" data-id="element-1216">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center font-bold text-xl" data-id="element-1217">
          A
        </div>
        <div className="flex-1 min-w-0" data-id="element-1218">
          <p className="font-bold text-white" data-id="element-1219">Ahmed Karim</p>
          <p className="text-xs text-gray-400 flex items-center gap-1" data-id="element-1220">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-1221" /> 4.92 ·{' '}
            {completedCount + 312} reviews
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3" data-id="element-1222">
        <StatCard label="Lifetime earnings" value="AED 84.5K" icon={TrendingUp} accent="emerald" data-id="element-1223" />
        <StatCard label="Completed jobs" value={String(completedCount + 312)} icon={CheckCircle2} accent="brand" data-id="element-1224" />
      </div>

      <section data-id="element-1225">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3" data-id="element-1226">
          Vehicle
        </h2>
        <div className="bg-gray-900/60 border border-white/5 rounded-2xl p-4 flex items-center gap-3" data-id="element-1227">
          <div className="w-10 h-10 rounded-lg bg-brand-500/15 text-brand-300 flex items-center justify-center" data-id="element-1228">
            <Truck className="w-5 h-5" data-id="element-1229" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-1230">
            <p className="font-bold text-white text-sm" data-id="element-1231">3-Ton Box Truck</p>
            <p className="text-xs text-gray-400" data-id="element-1232">
              D 84922 · Last serviced Aug 2
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-1" data-id="element-1233">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-2 px-1" data-id="element-1234">
          Settings
        </h2>
        <SettingsRow icon={Shield} label="License & verification" data-id="element-1235" />
        <SettingsRow icon={CreditCard} label="Payment & payouts" data-id="element-1236" />
        <SettingsRow icon={Settings} label="Preferences" data-id="element-1237" />
      </section>

      <button onClick={onSignOut} className="w-full py-3 rounded-2xl bg-white/5 hover:bg-white/10 text-red-400 font-bold text-sm transition-colors flex items-center justify-center gap-2" data-id="element-1238">
        <LogOut className="w-4 h-4" data-id="element-1239" /> Sign out
      </button>
    </div>;
}
function SettingsRow({
  icon: Icon,
  label
}: {
  icon: typeof Shield;
  label: string;
}) {
  return <button className="w-full bg-gray-900/40 hover:bg-gray-900/60 border border-white/5 rounded-xl p-3 flex items-center gap-3 transition-colors" data-id="element-1240">
      <div className="w-8 h-8 rounded-lg bg-white/5 text-gray-300 flex items-center justify-center" data-id="element-1241">
        <Icon className="w-4 h-4" data-id="element-1242" />
      </div>
      <span className="text-sm text-white flex-1 text-left" data-id="element-1243">{label}</span>
      <ArrowRight className="w-4 h-4 text-gray-500" data-id="element-1244" />
    </button>;
}
function JobDetailOverlay({
  job,
  onClose,
  onAdvance,
  onAskAi
}: {
  job: DriverJob;
  onClose: () => void;
  onAdvance: () => void;
  onAskAi: () => void;
}) {
  const pill = getStatusPill(job.status);
  const currentIdx = LIFECYCLE.findIndex(s => s.id === job.status);
  const isCompleted = job.status === 'completed';
  // For navigation: target pickup until we're picked, then dropoff
  const navTarget = ['accepted', 'in_progress', 'at_pickup'].includes(job.status) ? job.pickup : job.dropoff;
  const navHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(navTarget)}`;
  const waText = encodeURIComponent(`Hi ${job.customerName.split(' ')[0]}, this is your driver for ${job.service} (${job.id}). On my way to ${navTarget}.`);
  const waHref = `https://wa.me/${job.customerPhone.replace(/\D/g, '')}?text=${waText}`;
  const telHref = `tel:${job.customerPhone.replace(/\s/g, '')}`;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1245">
      <div onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-1246" />
      <motion.div initial={{
      y: 30,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 30,
      opacity: 0
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 32
    }} className="relative w-[375px] h-[812px] bg-black rounded-[3rem] shadow-2xl overflow-hidden border-[10px] border-gray-800 flex flex-col" data-id="element-1247">
        <div className="shrink-0 bg-gradient-to-b from-gray-900 to-black px-5 pt-12 pb-4 border-b border-white/5" data-id="element-1248">
          <div className="flex items-center justify-between mb-3" data-id="element-1249">
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Back" data-id="element-1250">
              <ChevronLeft className="w-5 h-5 text-white" data-id="element-1251" />
            </button>
            <span className="text-[10px] text-gray-400" data-id="element-1252">{job.id}</span>
          </div>
          <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${pill.className}`} data-id="element-1253">
            {pill.label}
          </span>
          <h2 className="text-lg font-display font-bold text-white mt-2" data-id="element-1254">
            {job.service}
          </h2>
          <p className="text-xs text-gray-400" data-id="element-1255">
            {job.scheduled} · AED {job.price}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-950 to-black p-5 space-y-5 scrollbar-hide" data-id="element-1256">
          {/* Customer */}
          <section data-id="element-1257">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-2" data-id="element-1258">
              Customer
            </h3>
            <div className="bg-gray-900/60 border border-white/5 rounded-2xl p-4" data-id="element-1259">
              <p className="font-bold text-white text-sm" data-id="element-1260">{job.customerName}</p>
              <p className="text-xs text-gray-400" data-id="element-1261">{job.customerPhone}</p>
              <div className="grid grid-cols-3 gap-2 mt-3" data-id="element-1262">
                <a href={telHref} className="flex flex-col items-center gap-1 py-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 ring-1 ring-emerald-400/20 transition-colors" data-id="element-1263">
                  <Phone className="w-4 h-4 text-emerald-300" data-id="element-1264" />
                  <span className="text-[10px] font-bold text-emerald-300" data-id="element-1265">
                    Call
                  </span>
                </a>
                <a href={waHref} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 py-2 rounded-xl bg-green-500/10 hover:bg-green-500/20 ring-1 ring-green-400/20 transition-colors" data-id="element-1266">
                  <MessageCircle className="w-4 h-4 text-green-300" data-id="element-1267" />
                  <span className="text-[10px] font-bold text-green-300" data-id="element-1268">
                    WhatsApp
                  </span>
                </a>
                <button onClick={onAskAi} className="flex flex-col items-center gap-1 py-2 rounded-xl bg-brand-500/10 hover:bg-brand-500/20 ring-1 ring-brand-400/20 transition-colors" data-id="element-1269">
                  <Sparkles className="w-4 h-4 text-brand-300" data-id="element-1270" />
                  <span className="text-[10px] font-bold text-brand-300" data-id="element-1271">
                    Ask AI
                  </span>
                </button>
              </div>
            </div>
          </section>

          {/* Route */}
          <section data-id="element-1272">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-2" data-id="element-1273">
              Route
            </h3>
            <div className="bg-gray-900/60 border border-white/5 rounded-2xl p-4 space-y-3" data-id="element-1274">
              <div className="flex items-start gap-2" data-id="element-1275">
                <MapPin className="w-4 h-4 text-brand-400 mt-0.5 shrink-0" data-id="element-1276" />
                <div className="flex-1 min-w-0" data-id="element-1277">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-1278">
                    Pickup
                  </p>
                  <p className="text-sm text-white" data-id="element-1279">{job.pickup}</p>
                </div>
              </div>
              <div className="flex items-start gap-2" data-id="element-1280">
                <MapPin className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" data-id="element-1281" />
                <div className="flex-1 min-w-0" data-id="element-1282">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-1283">
                    Dropoff
                  </p>
                  <p className="text-sm text-white" data-id="element-1284">{job.dropoff}</p>
                </div>
              </div>
              <a href={navHref} target="_blank" rel="noopener noreferrer" className="w-full py-2.5 rounded-xl bg-white text-black font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-gray-200 transition-colors" data-id="element-1285">
                <Navigation className="w-3.5 h-3.5" data-id="element-1286" />
                Navigate to{' '}
                {['accepted', 'in_progress', 'at_pickup'].includes(job.status) ? 'pickup' : 'dropoff'}
              </a>
            </div>
          </section>

          {/* Lifecycle */}
          <section data-id="element-1287">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-2" data-id="element-1288">
              Lifecycle
            </h3>
            <div className="bg-gray-900/60 border border-white/5 rounded-2xl p-4 space-y-3" data-id="element-1289">
              {LIFECYCLE.map((s, i) => {
              const reached = currentIdx >= i;
              const isCurrent = currentIdx === i;
              return <div key={s.id} className="flex items-center gap-3" data-id="element-1290">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${reached ? 'bg-brand-500 text-white' : 'bg-white/5 text-gray-600'} ${isCurrent ? 'ring-2 ring-brand-400/40' : ''}`} data-id="element-1291">
                      {reached ? <CheckCircle2 className="w-3 h-3" data-id="element-1292" /> : <span className="text-[10px] font-bold" data-id="element-1293">{i + 1}</span>}
                    </div>
                    <span className={`text-xs font-medium ${reached ? 'text-white' : 'text-gray-500'}`} data-id="element-1294">
                      {s.label}
                    </span>
                  </div>;
            })}
            </div>
          </section>
        </div>

        {/* Sticky CTA */}
        {!isCompleted && <div className="shrink-0 bg-black border-t border-white/5 p-5 pb-7" data-id="element-1295">
            <button onClick={onAdvance} className="w-full py-3.5 rounded-2xl bg-brand-500 hover:bg-brand-400 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2 shadow-lg shadow-brand-500/30" data-id="element-1296">
              {getPrimaryCTA(job.status)}
              <ArrowRight className="w-4 h-4" data-id="element-1297" />
            </button>
          </div>}
      </motion.div>
    </motion.div>;
}